import React, { useState, useRef } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const VerificationSettings = () => {
  const [verificationLevel, setVerificationLevel] = useState(3);
  const [kycStatus, setKycStatus] = useState('verified');
  const [uploadedDocuments, setUploadedDocuments] = useState({
    passport: {
      front: 'https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg?w=300&h=200',
      status: 'approved'
    },
    drivingLicense: {
      front: 'https://images.pexels.com/photos/7841828/pexels-photo-7841828.jpeg?w=300&h=200',
      back: 'https://images.pexels.com/photos/7841829/pexels-photo-7841829.jpeg?w=300&h=200',
      status: 'approved'
    },
    proofOfAddress: {
      document: 'https://images.pexels.com/photos/6863183/pexels-photo-6863183.jpeg?w=300&h=200',
      status: 'approved'
    }
  });
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRefs = {
    passport: useRef(null),
    license: useRef(null),
    address: useRef(null)
  };

  const verificationSteps = [
    {
      level: 1,
      title: 'Basic Information',
      description: 'Complete your profile with basic personal information',
      status: 'completed',
      requirements: ['Full name', 'Email address', 'Phone number', 'Date of birth']
    },
    {
      level: 2,
      title: 'Identity Verification',
      description: 'Upload government-issued ID for identity verification',
      status: 'completed',
      requirements: ['Government-issued ID', 'Clear photo of document', 'Document must be valid']
    },
    {
      level: 3,
      title: 'Address Verification',
      description: 'Provide proof of residence for address verification',
      status: 'completed',
      requirements: ['Utility bill', 'Bank statement', 'Government correspondence', 'Document within 3 months']
    },
    {
      level: 4,
      title: 'Enhanced Verification',
      description: 'Additional verification for higher trading limits',
      status: 'available',
      requirements: ['Video call verification', 'Additional documentation', 'Source of funds verification']
    }
  ];

  const documentTypes = [
    {
      id: 'passport',
      title: 'Passport',
      description: 'Upload your passport photo page',
      icon: 'FileText',
      required: true,
      status: uploadedDocuments.passport?.status || 'pending'
    },
    {
      id: 'drivingLicense',
      title: 'Driving License',
      description: 'Upload front and back of your driving license',
      icon: 'CreditCard',
      required: false,
      status: uploadedDocuments.drivingLicense?.status || 'pending'
    },
    {
      id: 'proofOfAddress',
      title: 'Proof of Address',
      description: 'Upload utility bill or bank statement (within 3 months)',
      icon: 'Home',
      required: true,
      status: uploadedDocuments.proofOfAddress?.status || 'pending'
    }
  ];

  const tradingLimits = [
    {
      level: 1,
      title: 'Basic Account',
      dailyLimit: '$1,000',
      monthlyLimit: '$10,000',
      features: ['Basic trading', 'Standard support']
    },
    {
      level: 2,
      title: 'Verified Account',
      dailyLimit: '$10,000',
      monthlyLimit: '$100,000',
      features: ['Advanced trading', 'Priority support', 'API access']
    },
    {
      level: 3,
      title: 'Premium Account',
      dailyLimit: '$50,000',
      monthlyLimit: '$500,000',
      features: ['All trading features', 'VIP support', 'Advanced API', 'Custom limits']
    },
    {
      level: 4,
      title: 'Institutional Account',
      dailyLimit: 'Unlimited',
      monthlyLimit: 'Unlimited',
      features: ['Institutional features', 'Dedicated manager', 'Custom solutions']
    }
  ];

  const handleFileUpload = (documentType, fileType = 'front') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setIsUploading(true);
        // Simulate upload
        setTimeout(() => {
          setUploadedDocuments(prev => ({
            ...prev,
            [documentType]: {
              ...prev[documentType],
              [fileType]: URL.createObjectURL(file),
              status: 'pending'
            }
          }));
          setIsUploading(false);
        }, 2000);
      }
    };
    input.click();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return 'text-success bg-success-100';
      case 'pending':
        return 'text-warning bg-warning-100';
      case 'rejected':
        return 'text-error bg-error-100';
      case 'available':
        return 'text-text-secondary bg-secondary-100';
      default:
        return 'text-text-secondary bg-secondary-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return 'CheckCircle';
      case 'pending':
        return 'Clock';
      case 'rejected':
        return 'XCircle';
      case 'available':
        return 'Circle';
      default:
        return 'Circle';
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-heading-semibold text-text-primary mb-1">Verification Settings</h2>
        <p className="text-sm text-text-secondary">Complete KYC/AML verification to unlock full trading features</p>
      </div>

      {/* Verification Progress */}
      <div className="mb-8 p-6 border border-border rounded-trading">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-heading-semibold text-text-primary mb-1">Verification Progress</h3>
            <p className="text-sm text-text-secondary">Current Level: {verificationLevel} of 4</p>
          </div>
          
          <div className="flex items-center space-x-2">
            <Icon name="Shield" size={20} className="text-success" />
            <span className={`px-3 py-1 rounded-trading text-sm font-body-medium ${getStatusColor(kycStatus)}`}>
              {kycStatus === 'verified' ? 'Verified' : 'Pending'}
            </span>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="space-y-4">
          {verificationSteps.map((step, index) => (
            <div key={step.level} className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  step.level <= verificationLevel 
                    ? 'bg-success text-white' 
                    : step.status === 'available' ?'bg-primary text-white' :'bg-secondary-200 text-text-secondary'
                }`}>
                  {step.level <= verificationLevel ? (
                    <Icon name="Check" size={16} />
                  ) : (
                    <span className="text-sm font-body-medium">{step.level}</span>
                  )}
                </div>
              </div>
              
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-body-medium text-text-primary">{step.title}</h4>
                  <span className={`px-2 py-1 rounded-trading text-xs font-body-medium ${getStatusColor(step.status)}`}>
                    {step.status}
                  </span>
                </div>
                <p className="text-sm text-text-secondary mb-2">{step.description}</p>
                <div className="flex flex-wrap gap-2">
                  {step.requirements.map((req, reqIndex) => (
                    <span key={reqIndex} className="px-2 py-1 bg-secondary-50 text-xs text-text-secondary rounded">
                      {req}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Document Upload */}
      <div className="mb-8 p-6 border border-border rounded-trading">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-6">Document Upload</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {documentTypes.map((docType) => (
            <div key={docType.id} className="border border-border rounded-trading p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center">
                    <Icon name={docType.icon} size={20} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-body-medium text-text-primary">{docType.title}</h4>
                    {docType.required && (
                      <span className="text-xs text-error">Required</span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-1">
                  <Icon name={getStatusIcon(docType.status)} size={16} className={getStatusColor(docType.status).split(' ')[0]} />
                </div>
              </div>
              
              <p className="text-sm text-text-secondary mb-4">{docType.description}</p>
              
              {/* Document Preview */}
              {uploadedDocuments[docType.id] && (
                <div className="mb-4">
                  {docType.id === 'drivingLicense' ? (
                    <div className="grid grid-cols-2 gap-2">
                      {uploadedDocuments[docType.id].front && (
                        <div className="aspect-video bg-secondary-50 rounded overflow-hidden">
                          <Image
                            src={uploadedDocuments[docType.id].front}
                            alt="License Front"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      {uploadedDocuments[docType.id].back && (
                        <div className="aspect-video bg-secondary-50 rounded overflow-hidden">
                          <Image
                            src={uploadedDocuments[docType.id].back}
                            alt="License Back"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="aspect-video bg-secondary-50 rounded overflow-hidden">
                      <Image
                        src={uploadedDocuments[docType.id].front || uploadedDocuments[docType.id].document}
                        alt={docType.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
              )}
              
              {/* Upload Buttons */}
              <div className="space-y-2">
                {docType.id === 'drivingLicense' ? (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleFileUpload(docType.id, 'front')}
                      disabled={isUploading}
                      className="flex items-center justify-center space-x-2 px-3 py-2 border border-border rounded-trading hover:bg-secondary-50 transition-colors duration-trading-fast disabled:opacity-50"
                    >
                      <Icon name="Upload" size={16} />
                      <span className="text-sm">Front</span>
                    </button>
                    <button
                      onClick={() => handleFileUpload(docType.id, 'back')}
                      disabled={isUploading}
                      className="flex items-center justify-center space-x-2 px-3 py-2 border border-border rounded-trading hover:bg-secondary-50 transition-colors duration-trading-fast disabled:opacity-50"
                    >
                      <Icon name="Upload" size={16} />
                      <span className="text-sm">Back</span>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleFileUpload(docType.id)}
                    disabled={isUploading}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 border border-border rounded-trading hover:bg-secondary-50 transition-colors duration-trading-fast disabled:opacity-50"
                  >
                    {isUploading ? (
                      <Icon name="Loader2" size={16} className="animate-spin" />
                    ) : (
                      <Icon name="Upload" size={16} />
                    )}
                    <span className="text-sm">Upload Document</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Trading Limits */}
      <div className="p-6 border border-border rounded-trading">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-6">Trading Limits</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tradingLimits.map((limit) => (
            <div key={limit.level} className={`p-4 border rounded-trading ${
              limit.level === verificationLevel 
                ? 'border-primary bg-primary-50' 
                : limit.level < verificationLevel
                ? 'border-success bg-success-50' :'border-border bg-surface'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-body-medium text-text-primary">{limit.title}</h4>
                {limit.level === verificationLevel && (
                  <span className="px-2 py-1 bg-primary text-white text-xs rounded-trading">Current</span>
                )}
                {limit.level < verificationLevel && (
                  <Icon name="CheckCircle" size={16} className="text-success" />
                )}
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-sm text-text-secondary">Daily Limit:</span>
                  <span className="text-sm font-body-medium text-text-primary">{limit.dailyLimit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-text-secondary">Monthly Limit:</span>
                  <span className="text-sm font-body-medium text-text-primary">{limit.monthlyLimit}</span>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-text-secondary mb-2">Features:</p>
                <ul className="space-y-1">
                  {limit.features.map((feature, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <Icon name="Check" size={12} className="text-success" />
                      <span className="text-sm text-text-secondary">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VerificationSettings;